import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { leasingApi } from '@/utils/leasingApi';
import { Button } from '@/components/ui/button';
import { ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TIER_STYLE = {
  low:    { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-700', label: 'Low risk' },
  medium: { bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-700',   label: 'Medium risk' },
  high:   { bg: 'bg-red-50',      border: 'border-red-200',     text: 'text-red-700',     label: 'High risk' },
};

const STAGE_PILL = {
  applicant: 'bg-violet-100 text-violet-800',
  approved:  'bg-emerald-100 text-emerald-800',
  rejected:  'bg-red-100 text-red-700',
};

export default function Applicants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screening, setScreening] = useState({}); // id -> bool

  const load = async () => {
    setLoading(true);
    try { const r = await leasingApi.listApplicants(); setRows(r.data || []); }
    catch (e) { toast.error('Failed to load applicants'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onScreen = async (a) => {
    setScreening(p => ({ ...p, [a.id]: true }));
    try {
      const r = await leasingApi.screenApplicant(a.id);
      toast.success(`Risk score: ${r.data.score} · ${TIER_STYLE[r.data.tier]?.label}`);
      load();
    } catch (e) { toast.error('Screening failed'); }
    finally { setScreening(p => ({ ...p, [a.id]: false })); }
  };

  const onDecide = async (a, to_stage) => {
    if (!window.confirm(`${to_stage === 'approved' ? 'Approve' : 'Reject'} applicant "${a.full_name}"?`)) return;
    try {
      await leasingApi.decideApplicant(a.id, { to_stage });
      toast.success(`${a.full_name} → ${to_stage}`);
      load();
    } catch (e) { toast.error('Decision failed'); }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="applicants-page">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0E1320]">Applicants</h1>
          <p className="text-[13px] text-[#71777E] mt-1">Formal applications · AI-powered tenant risk scoring · approve or reject with full audit trail.</p>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-10 text-[#71777E]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#E6EAF0] p-10 text-center text-[#71777E]">
              No applicants yet. Promote a Prospect to begin the application flow.
            </div>
          ) : rows.map(a => {
            const r = a.screening_result;
            const tier = r?.tier;
            const tierStyle = tier ? TIER_STYLE[tier] : null;
            return (
              <div key={a.id} data-testid={`applicant-card-${a.id}`} className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
                <div className="grid md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#ECEFF3]">
                  {/* Identity */}
                  <div className="p-4">
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Applicant</div>
                    <div className="mt-1 text-lg font-semibold text-[#0E1320]">{a.full_name}</div>
                    <div className="text-[12px] text-[#71777E] mt-1">{a.phone} · {a.email || 'no email'}</div>
                    <div className="mt-2.5 inline-flex items-center gap-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium reva-mono ${STAGE_PILL[a.lifecycle_stage] || ''}`}>{a.lifecycle_stage}</span>
                      {a.employer && <span className="text-[11px] text-[#71777E]">· {a.employer}</span>}
                    </div>
                    <dl className="mt-3 text-[12px] grid grid-cols-2 gap-y-1.5">
                      <dt className="text-[#71777E]">Income</dt><dd className="reva-mono text-end">SAR {(a.monthly_income_sar || 0).toLocaleString()}/mo</dd>
                      <dt className="text-[#71777E]">Employment</dt><dd className="reva-mono text-end">{a.employment_years || 0}y</dd>
                      <dt className="text-[#71777E]">Documents</dt><dd className="text-end text-[11.5px]">{(a.documents_uploaded || []).join(', ') || '—'}</dd>
                      <dt className="text-[#71777E]">Reference</dt><dd className="text-end">{a.previous_landlord_reference === true ? <span className="text-emerald-700">Verified</span> : a.previous_landlord_reference === false ? <span className="text-red-700">None</span> : '—'}</dd>
                    </dl>
                  </div>

                  {/* AI risk panel */}
                  <div className={`p-4 ${tierStyle?.bg || 'bg-[#FBFCFD]'}`} data-testid={`risk-panel-${a.id}`}>
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] inline-flex items-center gap-1.5">
                      <Sparkles size={11} className="text-[#00734A]" /> AI Tenant Risk Score
                    </div>
                    {r ? (
                      <>
                        <div className="mt-1.5 flex items-end gap-3">
                          <div>
                            <div className={`text-4xl font-bold tabular-nums ${tierStyle?.text || ''}`}>{r.score}</div>
                            <div className="text-[10.5px] reva-mono uppercase text-[#71777E] tracking-[0.14em]">/ 100</div>
                          </div>
                          <span className={`mb-1 px-2 py-0.5 rounded-full text-[11px] font-bold reva-mono uppercase tracking-[0.12em] ${tierStyle?.border} ${tierStyle?.text} bg-white border`}>
                            {tierStyle?.label}
                          </span>
                        </div>
                        {r.positive_signals?.length > 0 && (
                          <div className="mt-3">
                            <div className="text-[11px] font-semibold text-emerald-700 mb-1 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Positive signals</div>
                            <ul className="text-[12px] text-[#0E1320] space-y-0.5 ps-4 list-disc">
                              {r.positive_signals.map((s) => <li key={`pos-${s}`}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {r.risk_signals?.length > 0 && (
                          <div className="mt-2">
                            <div className="text-[11px] font-semibold text-red-700 mb-1 inline-flex items-center gap-1"><AlertTriangle size={11} /> Risk signals</div>
                            <ul className="text-[12px] text-[#0E1320] space-y-0.5 ps-4 list-disc">
                              {r.risk_signals.map((s) => <li key={`risk-${s}`}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="mt-2 text-[12.5px] text-[#71777E]">
                        Not screened yet. Click <strong>Run screening</strong> to compute the AI risk score.
                      </div>
                    )}
                  </div>

                  {/* Decision panel */}
                  <div className="p-4 flex flex-col">
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Action</div>
                    <div className="mt-3 space-y-2">
                      {!r && (
                        <Button onClick={() => onScreen(a)} disabled={screening[a.id]} className="w-full" data-testid={`applicant-screen-${a.id}`}>
                          {screening[a.id] ? <><Loader2 className="animate-spin h-3.5 w-3.5 mr-1.5" /> Screening…</> : <><ShieldCheck size={13} className="mr-1.5" /> Run screening</>}
                        </Button>
                      )}
                      {r && a.lifecycle_stage === 'applicant' && (
                        <>
                          <Button variant="outline" onClick={() => onScreen(a)} className="w-full" data-testid={`applicant-rescreen-${a.id}`}>
                            Re-run screening
                          </Button>
                          <Button onClick={() => onDecide(a, 'approved')} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`applicant-approve-${a.id}`}>
                            <CheckCircle2 size={13} className="mr-1.5" /> Approve
                          </Button>
                          <Button onClick={() => onDecide(a, 'rejected')} variant="outline" className="w-full text-red-700 border-red-200 hover:bg-red-50" data-testid={`applicant-reject-${a.id}`}>
                            <XCircle size={13} className="mr-1.5" /> Reject
                          </Button>
                        </>
                      )}
                      {['approved', 'rejected'].includes(a.lifecycle_stage) && (
                        <div className={`p-2.5 rounded-lg text-center text-[12px] font-medium ${a.lifecycle_stage === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                          Decision: {a.lifecycle_stage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
